import React from 'react';
import { Email } from './types';
import { validateEmail } from '../../../utils/validation';
import { CategoryDropdown } from '../category';
import '../../../components/common/CRMForm.css';

interface EmailSectionProps {
  emails: Email[];
  onChange: (emails: Email[]) => void;
}

const EmailSection: React.FC<EmailSectionProps> = ({ emails, onChange }) => {
  const addEmail = () => {
    const newEmail: Email = {
      id: 0, // 0 indicates a new email
      contactId: 0, // Will be set when saving
      typeId: 0, // No selection - user must choose
      emailAddress: '',
      isPrimary: emails.length === 0 // First email is primary by default
    };
    onChange([...emails, newEmail]);
  };

  const updateEmail = (index: number, field: keyof Email, value: any) => {
    const updatedEmails = emails.map((email, i) => {
      if (i === index) {
        const updatedEmail = { ...email, [field]: value };
        
        // If setting this email as primary, unset others
        if (field === 'isPrimary' && value) {
          // First unset all others
          const resetEmails = emails.map(e => ({ ...e, isPrimary: false }));
          resetEmails[index] = updatedEmail;
          return updatedEmail;
        }
        
        return updatedEmail;
      }
      
      // Unset primary for other emails if current email is being set as primary
      if (field === 'isPrimary' && value) {
        return { ...email, isPrimary: false };
      }
      
      return email;
    });
    
    onChange(updatedEmails);
  };

  const removeEmail = (index: number) => {
    const updatedEmails = emails.filter((_, i) => i !== index);
    
    // If we removed the primary email and there are still emails left,
    // make the first one primary
    if (emails[index].isPrimary && updatedEmails.length > 0) {
      updatedEmails[0].isPrimary = true;
    }
    
    onChange(updatedEmails);
  };

  const isValidEmailAddress = (emailAddress: string) => {
    return emailAddress.trim() === '' || validateEmail(emailAddress);
  };

  return (
    <div className="form-section-content">
      {emails.map((email, index) => (
        <div key={email.id} className="form-item-group">
          <div className="app-form-row">
            <div className="app-form-group">
              <label className="app-form-label">Email Type</label>
              <CategoryDropdown
                parentType="email"
                value={email.typeId}
                onChange={(value) => updateEmail(index, 'typeId', value)}
                required={true}
                className={email.typeId === 0 ? 'error' : ''}
              />
              {email.typeId === 0 && (
                <div className="app-form-error">Please select an email type</div>
              )}
            </div>

            <div className="app-form-group">
              <label className="app-form-label">Email Address</label>
              <input
                type="email"
                className={`app-form-input ${!isValidEmailAddress(email.emailAddress) ? 'error' : ''}`}
                value={email.emailAddress}
                onChange={(e) => updateEmail(index, 'emailAddress', e.target.value)}
                placeholder="example@domain.com"
              />
              {!isValidEmailAddress(email.emailAddress) && (
                <div className="app-form-error">Please enter a valid email address</div>
              )}
            </div>
          </div>

          <div className="app-form-group">
            <label className="app-checkbox-label">
              <input
                type="checkbox"
                className="app-form-checkbox"
                checked={email.isPrimary}
                onChange={(e) => updateEmail(index, 'isPrimary', e.target.checked)}
              />
              Primary email address
            </label>
          </div>

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn-delete"
              onClick={() => removeEmail(index)}
              title="Delete email"
            >
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="app-btn app-btn-secondary"
        onClick={addEmail}
      >
        Add Email Address
      </button>

      {emails.length > 0 && !emails.some(e => e.isPrimary) && (
        <div className="form-warning">
          No primary email address selected. Please mark one as primary.
        </div>
      )}
    </div>
  );
};

export default EmailSection;
